import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { MoreOption, MoreOptionAction } from '@components/more-options/more-options-dropdown.component';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import { ExhaustChannel } from 'icreate/common/models/graphs';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-cylinder-graph-table-row',
    templateUrl: './cylinder-graph-table-row.component.html',
    styleUrl: './cylinder-graph-table-row.component.css',
    standalone: false,
})
export class CylinderGraphTableRowComponent {
    @Input() rowIndex: number;
    @Input() channel: ExhaustChannel;
    @Input() vesselId: string;
    @Input() optionsVisible: boolean;
    @Output() onDeleteRow: EventEmitter<void> = new EventEmitter<void>();
    @Output() onOptionsToggled: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('otherOptions') otherOptions: ElementRef;
    @ViewChild('iconButton') iconButton: ElementRef;

    allowedTypesSet: Set<ChannelType> = getAnalogTypesSet();

    constructor(private dialog: MatDialog) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (
            this.otherOptions &&
            this.iconButton &&
            !this.otherOptions.nativeElement.contains(event.target) &&
            !this.iconButton.nativeElement.contains(event.target) &&
            this.optionsVisible
        ) {
            this.onOptionsToggled.emit(this.rowIndex);
        }
    }

    ngAfterViewInit() {
        document.addEventListener(
            'scroll',
            () => {
                if (this.optionsVisible) {
                    this.onOptionsToggled.emit(this.rowIndex);
                }
            },
            true
        );
    }

    async openChannelSelector(selector: Selector) {
        const isTemp = selector === Selector.TEMP;
        const isDeviation = selector === Selector.DEVIATION;

        let setShouldDisableTypes = false;
        let allowedTypesSet: Set<ChannelType>;
        let assignedChannel: Channel;

        assignedChannel = isTemp ? this.channel.tempChannel : isDeviation ? this.channel.deviationChannel : null;

        if (isTemp || isDeviation) {
            allowedTypesSet = isTemp ? getAnalogTypesSet() : new Set([ChannelType.EXH_GAS_DEV]);
            setShouldDisableTypes = true;
        }

        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vesselId,
                assignedChannel: assignedChannel,
                allowedTypesSet: allowedTypesSet,
                setShouldDisableTypes: setShouldDisableTypes,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (!channelSelected) return;
        if (isTemp) {
            this.channel.tempChannel = channelSelected;
        } else if (isDeviation) {
            this.channel.deviationChannel = channelSelected;
        }
    }

    openOptions(): void {
        this.onOptionsToggled.emit(this.rowIndex);
    }

    clear() {
        if (this.clearIsDisabled()) return;
        this.channel.tempChannel = null;
        this.channel.deviationChannel = null;
        this.onOptionsToggled.emit(this.rowIndex);
    }

    deleteRow() {
        this.onDeleteRow.emit();
        this.onOptionsToggled.emit(this.rowIndex);
    }

    clearIsDisabled() {
        return !this.channel?.tempChannel && !this.channel?.deviationChannel;
    }

    get moreOptions(): MoreOption[] {
        return [
            {
                action: MoreOptionAction.CLEAR,
                string: 'dlg.clear',
                disabled: this.clearIsDisabled(),
            },
            {
                action: MoreOptionAction.DELETE,
                string: 'dlg.delete',
            },
        ];
    }

    onMoreOptionSelected(action: MoreOptionAction): void {
        switch (action) {
            case MoreOptionAction.CLEAR:
                this.clear();
                break;

            case MoreOptionAction.DELETE:
                this.deleteRow();
                break;
        }
    }
}

enum Selector {
    UNDEFINED = 'undefined',
    TEMP = 'temp',
    DEVIATION = 'deviation',
}
